import Rate, {AndroidMarket} from 'react-native-rate';

import {createStorage} from '/util/async-storage';

import {
  APPLE_APP_ID,
  GOOGLE_PACKAGE_NAME,
  MIN_LOGGED_IN_TIMES_TO_SHOW_ALERT,
  MIN_TIME_PASSED_BEFORE_SHOW_ALERT,
  NUMBER_OF_TIMES_USER_LOGGED_IN,
  RATE_ALERT_SHOWN_BEFORE,
  SECOND_PROMPT_AFTER_LOGIN,
} from './rate.constant';

export const {getValue: getLoggedInNumber, setValue: setLoggedInNumber} =
  createStorage({
    key: NUMBER_OF_TIMES_USER_LOGGED_IN,
    defaultValue: 0,
  });

const {getValue: getAlertShownBefore, setValue: setAlertShownBefore} =
  createStorage({
    key: RATE_ALERT_SHOWN_BEFORE,
    defaultValue: false,
  });

let timeoutId: ReturnType<typeof setTimeout> | null = null; // Declare a timeoutId variable to hold the current timeout reference

/**
 * Tracks user login sessions and decides if the review prompt should be shown.
 */
export const trackLogin = async () => {
  const loggedInNumber = await getLoggedInNumber();
  // Increment session count after every login
  await setLoggedInNumber(loggedInNumber + 1);
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  timeoutId = setTimeout(async () => {
    await checkReviewPrompt();
  }, MIN_TIME_PASSED_BEFORE_SHOW_ALERT);
};

/**
 * Checks if the review prompt should be shown.
 */
const checkReviewPrompt = async () => {
  const loggedInNumber = await getLoggedInNumber();
  const alertShownBefore = await getAlertShownBefore();

  // Check if the user has already been prompted and reviewed
  if (alertShownBefore) {
    return;
  }

  // Prompt on the 2nd login
  if (loggedInNumber === MIN_LOGGED_IN_TIMES_TO_SHOW_ALERT) {
    const userReviewed = await promptReview();
    if (userReviewed) {
      await setAlertShownBefore(true);
      return;
    }
  }

  // Prompt again on the 4th login if the review was not submitted
  if (loggedInNumber === SECOND_PROMPT_AFTER_LOGIN) {
    const userReviewed = await promptReview();
    if (userReviewed) {
      await setAlertShownBefore(true);
    }
  }
};

/**
 * Triggers the in-app review prompt.
 */
const promptReview = (): Promise<boolean> => {
  return new Promise(resolve => {
    const options = {
      AppleAppID: APPLE_APP_ID,
      GooglePackageName: GOOGLE_PACKAGE_NAME,
      preferInApp: true,
      openAppStoreIfInAppFails: false,
      inAppDelay: 1.0,
      preferredAndroidMarket: AndroidMarket.Google,
    };

    Rate.rate(options, success => {
      resolve(success);
    });
  });
};
